import { For, Show, createMemo, type Component } from 'solid-js';
import type { CasinoProfile, TableProfile } from '~/domain/types';
import type { Strategy } from '~/domain/strategy';
import type { LowerStop, SessionRules, UpperStop } from '~/domain/session';
import { formatCents } from '~/domain/profiles';

/**
 * Simulation setup (SPEC-V2.md §3.3, §5.1).
 *
 * Everything variable lives here rather than on a strategy. A buy-in and a
 * walk-away marker are properties of a *test*, not of the arrangement being
 * tested, so they are set once and shared by every entry — hold them constant
 * and a difference in outcome is the strategy.
 *
 * The chip amount is the deliberate exception, set per selected strategy,
 * because stake size is itself worth testing. The cost is that an entry
 * differing in both layout and stake cannot be attributed to either, which the
 * app discloses rather than resolves: the chip is a column of the comparison
 * table and it is never collapsed (§9.2).
 */

export interface SelectedEntry {
  readonly strategyId: string;
  readonly denominationCents: number;
}

export interface SimulationSetupProps {
  strategies: readonly Strategy[];
  selected: readonly SelectedEntry[];
  casino: CasinoProfile;
  table: TableProfile;
  rules: SessionRules;
  sessionsPerCampaign: number;
  campaigns: number;
  seed: string;
  running: boolean;
  progress: number;
  onToggle: (strategyId: string) => void;
  onDenomination: (strategyId: string, cents: number) => void;
  onRules: (rules: SessionRules) => void;
  onSessionsPerCampaign: (value: number) => void;
  onCampaigns: (value: number) => void;
  onSeed: (seed: string) => void;
  onRun: () => void;
  onCancel: () => void;
  class?: string;
}

const SimulationSetup: Component<SimulationSetupProps> = (props) => {
  const chosen = createMemo(() => new Map(props.selected.map((entry) => [entry.strategyId, entry])));

  const patch = (changes: Partial<SessionRules>) => props.onRules({ ...props.rules, ...changes });

  const setLower = (kind: LowerStop['kind']) =>
    patch({
      lower:
        kind === 'stop-loss'
          ? { kind, floorCents: Math.round(props.rules.buyInCents / 4) }
          : { kind },
    });

  const setUpper = (kind: UpperStop['kind']) =>
    patch({
      upper:
        kind === 'pocket-target'
          ? { kind, cents: props.rules.buyInCents }
          : kind === 'spins'
            ? { kind, spins: 60 }
            : { kind },
    });

  return (
    <div class={`space-y-4 ${props.class ?? ''}`}>
      <section class="rounded-lg border border-line bg-surface-1 p-3">
        <h3 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Strategies to run
        </h3>
        <Show
          when={props.strategies.length > 0}
          fallback={
            <p class="text-[11px] text-ink-faint">
              No saved strategies for this wheel. Stack some spreads in Setup first.
            </p>
          }
        >
          <div class="space-y-1">
            <For each={props.strategies}>
              {(strategy) => {
                const entry = () => chosen().get(strategy.id);
                return (
                  <div
                    class="rounded border px-2 py-1.5 transition-colors"
                    classList={{
                      'border-placed bg-placed/10': entry() !== undefined,
                      'border-line bg-surface-2': entry() === undefined,
                    }}
                  >
                    <label class="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={entry() !== undefined}
                        onChange={() => props.onToggle(strategy.id)}
                        class="accent-[var(--color-placed)]"
                      />
                      <span class="flex-1 truncate text-[11px] text-ink">{strategy.name}</span>
                      <span class="tnum shrink-0 text-[10px] text-ink-faint">
                        {strategy.layers.length}{' '}
                        {strategy.layers.length === 1 ? 'spread' : 'spreads'}
                      </span>
                    </label>

                    {/* The stake is per strategy because stake size is one of
                        the things under test, not a nuisance to hold fixed. */}
                    <Show when={entry()}>
                      {(chosenEntry) => (
                        <div class="mt-1.5 flex flex-wrap items-center gap-1 pl-6">
                          <span class="text-[10px] text-ink-faint">Chip</span>
                          <For each={props.casino.denominations}>
                            {(cents) => (
                              <button
                                type="button"
                                onClick={() => props.onDenomination(strategy.id, cents)}
                                class="tnum rounded-full border px-2 py-0.5 text-[10px] transition-colors"
                                classList={{
                                  'border-placed-strong bg-placed text-white':
                                    chosenEntry().denominationCents === cents,
                                  'border-line bg-surface-2 text-ink-dim hover:border-line-strong':
                                    chosenEntry().denominationCents !== cents,
                                }}
                              >
                                {formatCents(cents, props.casino.currency)}
                              </button>
                            )}
                          </For>
                        </div>
                      )}
                    </Show>
                  </div>
                );
              }}
            </For>
          </div>
        </Show>
      </section>

      <section class="rounded-lg border border-line bg-surface-1 p-3">
        <h3 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Walk-away markers
        </h3>
        <p class="mb-2.5 text-[10px] leading-relaxed text-ink-faint">
          Shared by every strategy in the run. Held constant, a difference in the results
          is the strategy rather than the rules it happened to be given.
        </p>

        <div class="space-y-2.5">
          <Money
            label="Buy-in"
            hint="A hard cap. Never topped up mid-session."
            cents={props.rules.buyInCents}
            currency={props.casino.currency}
            onChange={(cents) => patch({ buyInCents: cents })}
          />
          <Money
            label="Pocket above"
            hint="Winnings past this are skimmed and never staked again."
            cents={props.rules.pocketThresholdCents}
            currency={props.casino.currency}
            onChange={(cents) => patch({ pocketThresholdCents: cents })}
          />

          <div>
            <span class="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
              Stop while losing
            </span>
            <div class="flex gap-1">
              <For each={['bust', 'stop-loss'] as const}>
                {(kind) => (
                  <Choice
                    active={props.rules.lower.kind === kind}
                    label={kind === 'bust' ? 'Play to bust' : 'Stop-loss'}
                    onClick={() => setLower(kind)}
                  />
                )}
              </For>
            </div>
            <Show when={props.rules.lower.kind === 'stop-loss'}>
              <div class="mt-1.5">
                <Money
                  label="Walk when down to"
                  cents={
                    props.rules.lower.kind === 'stop-loss' ? props.rules.lower.floorCents : 0
                  }
                  currency={props.casino.currency}
                  onChange={(cents) => patch({ lower: { kind: 'stop-loss', floorCents: cents } })}
                />
              </div>
            </Show>
          </div>

          <div>
            <span class="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
              Stop while ahead
            </span>
            <div class="flex gap-1">
              <For each={['none', 'pocket-target', 'spins'] as const}>
                {(kind) => (
                  <Choice
                    active={props.rules.upper.kind === kind}
                    label={kind === 'none' ? 'None' : kind === 'spins' ? 'Spin count' : 'Target'}
                    onClick={() => setUpper(kind)}
                  />
                )}
              </For>
            </div>
            <Show when={props.rules.upper.kind === 'pocket-target'}>
              <div class="mt-1.5">
                <Money
                  label="Walk once pocketed"
                  cents={props.rules.upper.kind === 'pocket-target' ? props.rules.upper.cents : 0}
                  currency={props.casino.currency}
                  onChange={(cents) => patch({ upper: { kind: 'pocket-target', cents } })}
                />
              </div>
            </Show>
            <Show when={props.rules.upper.kind === 'spins'}>
              <div class="mt-1.5">
                <Count
                  label="Walk after"
                  value={props.rules.upper.kind === 'spins' ? props.rules.upper.spins : 0}
                  onChange={(spins) => patch({ upper: { kind: 'spins', spins } })}
                />
              </div>
            </Show>
          </div>
        </div>
      </section>

      <section class="rounded-lg border border-line bg-surface-1 p-3">
        <h3 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          How much to run
        </h3>
        <div class="grid grid-cols-2 gap-2">
          <Count
            label="Sessions each"
            value={props.sessionsPerCampaign}
            onChange={props.onSessionsPerCampaign}
          />
          <Count label="Campaigns" value={props.campaigns} onChange={props.onCampaigns} />
        </div>
        <label class="mt-2 block">
          <span class="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
            Seed
          </span>
          <input
            type="text"
            value={props.seed}
            onInput={(event) => props.onSeed(event.currentTarget.value)}
            class="tnum w-full rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink"
          />
        </label>
        <p class="mt-1 text-[10px] leading-relaxed text-ink-faint">
          Every strategy runs on this seed, so the same session sees the same spins for
          all of them. That is the only comparison that says anything.
        </p>
      </section>

      <Show
        when={!props.running}
        fallback={
          <div class="space-y-1.5">
            <div class="h-1.5 overflow-hidden rounded bg-surface-3">
              <div
                class="h-full bg-placed transition-[width]"
                style={{ width: `${Math.round(props.progress * 100)}%` }}
              />
            </div>
            <button
              type="button"
              onClick={props.onCancel}
              class="w-full rounded border border-line bg-surface-2 px-2 py-1.5 text-[11px] text-ink-dim hover:border-loss hover:text-loss"
            >
              Cancel
            </button>
          </div>
        }
      >
        <button
          type="button"
          onClick={props.onRun}
          disabled={props.selected.length === 0}
          class="w-full rounded border border-placed-strong bg-placed px-2 py-1.5 text-[11px] font-medium text-white enabled:hover:bg-placed-strong disabled:opacity-40"
        >
          {props.selected.length === 0
            ? 'Choose a strategy to run'
            : `Run ${props.selected.length} ${props.selected.length === 1 ? 'strategy' : 'strategies'}`}
        </button>
      </Show>
    </div>
  );
};

const Choice: Component<{ active: boolean; label: string; onClick: () => void }> = (props) => (
  <button
    type="button"
    onClick={props.onClick}
    class="flex-1 rounded border px-2 py-1 text-[10px] transition-colors"
    classList={{
      'border-placed bg-placed/20 text-ink': props.active,
      'border-line bg-surface-2 text-ink-dim hover:text-ink': !props.active,
    }}
  >
    {props.label}
  </button>
);

/**
 * Money in, integer cents out. Typed as currency and stored to the cent, so a
 * $2.50 threshold stays exact — the app has no floats anywhere in it.
 */
const Money: Component<{
  label: string;
  hint?: string;
  cents: number;
  currency: string;
  onChange: (cents: number) => void;
}> = (props) => (
  <label class="block">
    <span class="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
      {props.label}
      <Show when={props.hint}>
        <span class="ml-1.5 normal-case tracking-normal opacity-80">{props.hint}</span>
      </Show>
    </span>
    <input
      type="number"
      min="0"
      step="1"
      value={Math.round(props.cents / 100)}
      onInput={(event) => {
        const value = Number(event.currentTarget.value);
        if (Number.isFinite(value) && value >= 0) props.onChange(Math.round(value) * 100);
      }}
      class="tnum w-full rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink"
    />
  </label>
);

const Count: Component<{ label: string; value: number; onChange: (value: number) => void }> = (
  props,
) => (
  <label class="block">
    <span class="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
      {props.label}
    </span>
    <input
      type="number"
      min="1"
      step="1"
      value={props.value}
      onInput={(event) => {
        const value = Number(event.currentTarget.value);
        if (Number.isInteger(value) && value >= 1) props.onChange(value);
      }}
      class="tnum w-full rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink"
    />
  </label>
);

export default SimulationSetup;
